/**
 * Responsive <picture> wrapper that serves a WebP with a JPG fallback.
 *
 * The `src` is the original JPG path (kept as the universal fallback for the
 * ~2% of browsers without WebP); the WebP sibling is derived by swapping the
 * extension. WebP assets are produced at build time by
 * scripts/generate-assets.mjs — keep the two in sync.
 */
interface PictureProps {
  /** JPG source path, e.g. "/images/hero-dog.jpg". */
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  /** Eager+high priority for the LCP image; lazy for the rest. */
  priority?: boolean;
}

export function Picture({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
}: PictureProps): JSX.Element {
  const webp = src.replace(/\.jpe?g$/i, ".webp");
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding={priority ? "sync" : "async"}
      />
    </picture>
  );
}
